import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PageHeader, Card, Field, SalesIdChip } from "../../components/common";
import { api, errMsg, newKey } from "../../lib/api";
import { pkr, commissionText, commissionPerUnit } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";

const EMPTY = { quantity: 1, customer_name: "", customer_phone: "", customer_email: "", delivery_address: "", notes: "", consent: false };

function Confirmation({ order, onNew }) {
  return (
    <Card className="mx-auto max-w-xl p-8 text-center" data-testid="order-confirmation">
      <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
      <h2 className="mt-4 font-heading text-2xl font-bold">Order submitted</h2>
      <p className="mt-2 text-slate-600">Your order has been registered. The administrator will confirm it and update the status. No payment has been taken online.</p>
      <div className="mt-6 space-y-2 rounded-xl bg-slate-50 p-5 text-left text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Order number</span><span className="font-mono font-bold" data-testid="confirmation-order-no">{order.order_no}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Deal</span><span className="font-medium">{order.deal.name}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Customer</span><span>{order.customer.name}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-mono font-bold">{pkr(order.total_amount)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Expected commission</span><span className="font-mono font-bold text-emerald-700">{pkr(order.commission_amount)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Sales ID</span><span className="font-mono">{order.sales_id}</span></div>
      </div>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <Button asChild variant="outline" className="h-11 flex-1"><Link to={`/app/orders/${order.id}`} data-testid="confirmation-view-order">View order</Link></Button>
        <Button className="h-11 flex-1" onClick={onNew} data-testid="confirmation-new-order">Book another order</Button>
      </div>
    </Card>
  );
}

export default function BookOrder() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [dealId, setDealId] = useState(params.get("deal") || "");
  const [f, setF] = useState(EMPTY);
  const [key, setKey] = useState(newKey());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  useEffect(() => { api.get("/deals", { params: { page_size: 100 } }).then((r) => setDeals(r.data.items.filter((d) => d.availability !== "out_of_stock"))); }, []);
  const deal = useMemo(() => deals.find((d) => d.id === dealId), [deals, dealId]);
  const qty = Math.max(1, parseInt(f.quantity) || 1);
  const total = deal ? deal.price * qty : 0;
  const estCommission = deal ? (deal.commission_type === "fixed" ? deal.commission_value * qty : Math.round((total * deal.commission_value) / 100)) : 0;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!deal) return setErr("Please select a deal");
    if (!f.consent) return setErr("Please confirm the customer agreed to share their details");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { ...f, quantity: qty, deal_id: dealId, customer_email: f.customer_email || null, idempotency_key: key });
      setDone(data.order);
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  const reset = () => { setDone(null); setF(EMPTY); setKey(newKey()); };
  if (done) return <Confirmation order={done} onNew={reset} />;

  return (
    <div>
      <PageHeader title="Book an Order" subtitle="Enter your customer's details. Prices are fixed and calculated automatically." />
      <form onSubmit={submit} className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="space-y-4 p-5">
            <h2 className="font-heading text-lg font-semibold">1. Choose deal</h2>
            <Field label="Deal" required>
              <Select value={dealId} onValueChange={setDealId}>
                <SelectTrigger className="h-12" data-testid="book-deal-select"><SelectValue placeholder="Select a published deal" /></SelectTrigger>
                <SelectContent>{deals.map((d) => <SelectItem key={d.id} value={d.id} data-testid={`book-deal-option-${d.ref}`}>{d.name} — {pkr(d.price)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {deal && <p className="text-sm text-slate-600">Unit price <b className="font-mono">{pkr(deal.price)}</b> · Commission <b className="font-mono text-emerald-700">{commissionText(deal)}</b>{deal.commission_type === "percent" && ` (≈ ${pkr(commissionPerUnit(deal))}/unit)`}</p>}
            <Field label="Quantity" required><Input data-testid="book-quantity-input" type="number" min={1} max={1000} value={f.quantity} onChange={set("quantity")} className="h-12 w-32" /></Field>
          </Card>
          <Card className="space-y-4 p-5">
            <h2 className="font-heading text-lg font-semibold">2. Customer details</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Customer full name" required><Input data-testid="book-customer-name" required value={f.customer_name} onChange={set("customer_name")} className="h-12" /></Field>
              <Field label="Customer phone" required><Input data-testid="book-customer-phone" required placeholder="03XX-XXXXXXX" value={f.customer_phone} onChange={set("customer_phone")} className="h-12" /></Field>
            </div>
            <Field label="Customer email (optional)"><Input data-testid="book-customer-email" type="email" value={f.customer_email} onChange={set("customer_email")} className="h-12" /></Field>
            <Field label="Delivery address" required={deal?.requires_delivery} hint={deal && !deal.requires_delivery ? "Not required for this deal" : ""}>
              <Textarea data-testid="book-delivery-address" rows={3} value={f.delivery_address} onChange={set("delivery_address")} required={!!deal?.requires_delivery} />
            </Field>
            <Field label="Additional notes"><Textarea data-testid="book-notes" rows={2} value={f.notes} onChange={set("notes")} /></Field>
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <input type="checkbox" data-testid="book-consent-checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-0.5 h-5 w-5 accent-emerald-600" />
              The customer has agreed to this order and to share their details with CommissionWorld for processing and delivery.
            </label>
          </Card>
        </div>
        <div>
          <Card className="sticky top-20 space-y-4 p-5">
            <h2 className="font-heading text-lg font-semibold">Order summary</h2>
            <div className="flex items-center justify-between text-sm"><span className="text-slate-500">Sales ID</span><SalesIdChip id={user.sales_id} /></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Unit price</span><span className="font-mono">{pkr(deal?.price)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Quantity</span><span className="font-mono">{qty}</span></div>
            <div className="flex justify-between border-t pt-3"><span className="font-semibold">Total</span><span className="font-mono text-xl font-bold" data-testid="book-total">{pkr(total)}</span></div>
            <div className="flex justify-between rounded-lg bg-emerald-50 p-3 text-sm"><span className="text-emerald-800">Expected commission</span><span className="font-mono font-bold text-emerald-700" data-testid="book-commission">{pkr(estCommission)}</span></div>
            <p className="text-xs text-slate-500">Commission becomes payable only after the order is completed, payment is verified and the administrator approves it.</p>
            {err && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="book-error">{err}</div>}
            <Button data-testid="book-submit-button" disabled={busy} className="h-12 w-full text-base">{busy ? "Submitting…" : "Submit order"}</Button>
          </Card>
        </div>
      </form>
    </div>
  );
}
